import { db } from '../database/db'

// Fonction pour générer un UUID v4
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

class ConflictResolver {
  ensureUUID(entity) {
    if (!entity.uuid) {
      entity.uuid = generateUUID()
    }
    return entity
  }

  ensureTimestamps(entity, isNew = false) {
    const now = new Date().toISOString()
    if (isNew || !entity.createdAt) {
      entity.createdAt = now
    }
    entity.updatedAt = now
    return entity
  }

  areProductsEqual(product1, product2) {
    return (
      product1.code === product2.code &&
      product1.name === product2.name &&
      product1.basePrice === product2.basePrice
    )
  }

  arePurchasesEqual(purchase1, purchase2) {
    return (
      purchase1.clientName === purchase2.clientName &&
      purchase1.date === purchase2.date &&
      purchase1.totalAmount === purchase2.totalAmount &&
      purchase1.clientPhone === purchase2.clientPhone
    )
  }

  // Trouve un produit existant par UUID ou par code (dans la boutique actuelle)
  async findExistingProduct(product, shopId) {
    try {
      if (!shopId) return { found: false }

      // 1. Recherche par UUID dans la boutique
      if (product.uuid) {
        const byUuid = await db.products
          .where('uuid')
          .equals(product.uuid)
          .and((p) => p.shopId === shopId)
          .first()
        if (byUuid) {
          return { found: true, match: byUuid, matchType: 'uuid' }
        }
      }

      // 2. Recherche par code dans la boutique
      const byCode = await db.products
        .where('[shopId+code]')
        .equals([shopId, product.code])
        .first()

      if (byCode) {
        if (this.areProductsEqual(product, byCode)) {
          return { found: true, match: byCode, matchType: 'business_key' }
        }
        return { found: true, match: byCode, matchType: 'code_conflict' }
      }

      return { found: false }
    } catch (error) {
      console.error('Erreur recherche produit:', error)
      return { found: false }
    }
  }

  async findExistingPurchase(purchase, shopId) {
    try {
      if (!shopId) return { found: false }

      // 1. Recherche par UUID dans la boutique
      if (purchase.uuid) {
        const byUuid = await db.purchases
          .where('uuid')
          .equals(purchase.uuid)
          .and((p) => p.shopId === shopId)
          .first()
        if (byUuid) {
          return { found: true, match: byUuid, matchType: 'uuid' }
        }
      }

      // 2. Recherche par clé métier dans la boutique
      const candidates = await db.purchases
        .where('shopId')
        .equals(shopId)
        .and(
          (p) =>
            p.clientName === purchase.clientName &&
            p.date === purchase.date &&
            p.totalAmount === purchase.totalAmount,
        )
        .toArray()

      if (candidates.length > 0) {
        const exactMatch = candidates.find((c) =>
          this.arePurchasesEqual(purchase, c),
        )
        if (exactMatch) {
          return { found: true, match: exactMatch, matchType: 'business_key' }
        }
      }

      return { found: false }
    } catch (error) {
      console.error('Erreur recherche achat:', error)
      return { found: false }
    }
  }

  // Génère un code unique dans la boutique actuelle
  async generateUniqueProductCode(baseCode, shopId) {
    if (!shopId) return baseCode

    let code = baseCode
    let counter = 1

    while (
      (await db.products
        .where('[shopId+code]')
        .equals([shopId, code])
        .count()) > 0
    ) {
      code = `${baseCode}_${counter}`
      counter++
    }

    return code
  }

  async detectProductConflicts(products, shopId) {
    if (!shopId) return []

    const conflicts = []

    for (let product of products) {
      product = this.ensureUUID(product)
      product = this.ensureTimestamps(product, false)

      const existing = await this.findExistingProduct(product, shopId)

      if (
        existing.found &&
        (existing.matchType === 'uuid' ||
          existing.matchType === 'business_key' ||
          existing.matchType === 'code_conflict')
      ) {
        conflicts.push({
          type: 'product',
          imported: product,
          existing: existing.match,
          matchType: existing.matchType,
          reason:
            existing.matchType === 'code_conflict'
              ? 'Même code mais données différentes'
              : 'Produit existant trouvé',
        })
      }
    }

    return conflicts
  }

  async detectPurchaseConflicts(purchases, shopId) {
    if (!shopId) return []

    const conflicts = []

    for (let purchase of purchases) {
      purchase = this.ensureUUID(purchase)
      purchase = this.ensureTimestamps(purchase, false)

      const existing = await this.findExistingPurchase(purchase, shopId)

      if (
        existing.found &&
        (existing.matchType === 'uuid' || existing.matchType === 'business_key')
      ) {
        conflicts.push({
          type: 'purchase',
          imported: purchase,
          existing: existing.match,
          matchType: existing.matchType,
          reason: 'Achat existant trouvé',
        })
      }
    }

    return conflicts
  }

  async applyProductResolutions(products, resolutions, shopId) {
    if (!shopId) {
      throw new Error('ShopId requis pour appliquer les résolutions')
    }

    const results = {
      toAdd: [],
      toUpdate: [],
      toSkip: [],
    }

    for (let product of products) {
      product = this.ensureUUID(product)
      product = this.ensureTimestamps(product, false)
      product.shopId = shopId // Assurer shopId

      const existing = await this.findExistingProduct(product, shopId)
      const resolution = resolutions.find(
        (r) =>
          r.conflict.imported.uuid === product.uuid ||
          r.conflict.imported.code === product.code,
      )

      if (!existing.found) {
        const { id, ...productWithoutId } = product
        results.toAdd.push(productWithoutId)
      } else if (resolution) {
        switch (resolution.decision) {
          case 'keep':
            results.toSkip.push(product)
            break

          case 'replace':
            const { id: _, ...productData } = product
            results.toUpdate.push({
              ...productData,
              id: existing.match.id,
            })
            break

          case 'add_new':
            const newCode = await this.generateUniqueProductCode(
              product.code,
              shopId,
            )
            const { id: __, uuid, ...newProductData } = product
            newProductData.code = newCode
            newProductData.uuid = generateUUID()
            newProductData.shopId = shopId
            results.toAdd.push(newProductData)
            break
        }
      } else {
        const { id, ...productWithoutId } = product
        results.toAdd.push(productWithoutId)
      }
    }

    return results
  }

  async applyPurchaseResolutions(purchases, resolutions, shopId) {
    if (!shopId) {
      throw new Error('ShopId requis pour appliquer les résolutions')
    }

    const results = {
      toAdd: [],
      toUpdate: [],
      toSkip: [],
    }

    for (let purchase of purchases) {
      purchase = this.ensureUUID(purchase)
      purchase = this.ensureTimestamps(purchase, false)
      purchase.shopId = shopId // Assurer shopId

      const existing = await this.findExistingPurchase(purchase, shopId)
      const resolution = resolutions.find(
        (r) => r.conflict.imported.uuid === purchase.uuid,
      )

      if (!existing.found) {
        const { id, ...purchaseWithoutId } = purchase
        results.toAdd.push(purchaseWithoutId)
      } else if (resolution) {
        switch (resolution.decision) {
          case 'keep':
            results.toSkip.push(purchase)
            break

          case 'replace':
            const { id: _, ...purchaseData } = purchase
            results.toUpdate.push({
              ...purchaseData,
              id: existing.match.id,
            })
            break

          case 'add_new':
            const { id: __, uuid, ...newPurchaseData } = purchase
            newPurchaseData.uuid = generateUUID()
            newPurchaseData.shopId = shopId
            results.toAdd.push(newPurchaseData)
            break
        }
      } else {
        const { id, ...purchaseWithoutId } = purchase
        results.toAdd.push(purchaseWithoutId)
      }
    }

    return results
  }

  async detectAllConflicts(importData, shopId) {
    if (!shopId) return []

    const { products = [], purchases = [] } = importData

    const productConflicts = await this.detectProductConflicts(products, shopId)
    const purchaseConflicts = await this.detectPurchaseConflicts(
      purchases,
      shopId,
    )

    return [...productConflicts, ...purchaseConflicts]
  }
}

export default new ConflictResolver()
export { generateUUID }
